import { useState, useMemo } from "react";
import { t, Trans } from "@lingui/macro";
import copy from "img/ic_copy_16.svg";
import externalLink from "img/ic_new_link_16.svg";
import { helperToast } from "lib/helperToast";
import { shortenAddress, useENS } from "lib/legacy";
import { createBreakpoint, useCopyToClipboard } from "react-use";
import "./AddressDropdown.css";
import ExternalLink from "components/ExternalLink/ExternalLink";
import shevronDown from "img/shevron-down-circle.svg";
import { identicon } from "minidenticons";
import { useWeb3React } from "@web3-react/core";

type Props = {
  account: string;
  accountUrl: string;
  disconnectAccountAndCloseSettings: () => void;
};

function AddressDropdown({ account, accountUrl, disconnectAccountAndCloseSettings }: Props) {
  const useBreakpoint = createBreakpoint({ L: 600, M: 550, S: 400 });
  const breakpoint = useBreakpoint();
  const [, copyToClipboard] = useCopyToClipboard();
  const { ensName } = useENS(account);
  const { active } = useWeb3React();
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const IdenticonImg = ({ username, saturation, lightness, ...props }) => {
    const svgURI = useMemo(
      () => "data:image/svg+xml;utf8," + encodeURIComponent(identicon(username, saturation, lightness)),
      [username, saturation, lightness]
    );
    return <img src={svgURI} alt={username} {...props} />;
  };

  return (
    <div className="address-dropdown transition" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="App-cta small transparent address-btn">
        <div className="user-avatar">
          {active && (
            <IdenticonImg
              username={account}
              saturation="80"
              lightness="50"
              width="25"
              height="25"
              className="Identicon"
            />
          )}
        </div>
        <span className="user-address-btn ">{ensName || shortenAddress(account, breakpoint === "S" ? 9 : 13)}</span>

        <div className="address-dropdown-shevron-wrap">
          <img src={shevronDown} alt="" />
        </div>

        {isHovered && (
          <div className="address-menu-items">
            <div className="address-section">
              <p className="wallet-provider-wrapper">
                <Trans>Account </Trans>
                <span className="wallet-provider">
                  <Trans>(Metamask)</Trans>
                </span>
              </p>

              <button onClick={disconnectAccountAndCloseSettings} className="disconnect-btn transition">
                <Trans>Disconnect</Trans>
              </button>
            </div>

            <div className="address-section">
              <div className="user-avatar Identicon-wrapper">
                {active && (
                  <IdenticonImg
                    username={account}
                    saturation="80"
                    lightness="50"
                    width="40"
                    height="40"
                    className="Identicon"
                  />
                )}
              </div>

              <div className="user-address-wrapper">
                <span className="user-address strong">{ensName || account}</span>
              </div>
            </div>

            <div className="address-bottom">
              <div className="wallet-menu-item">
                <div className="address-bottom-item">
                  <ExternalLink href={accountUrl}>
                    <p className="address-link transition">View in Explorer</p>
                  </ExternalLink>

                  <div className="address-icons-link ">
                    <img src={externalLink} alt="Open address in explorer" />
                  </div>
                </div>
              </div>

              <div
                className="address-bottom-item"
                onClick={() => {
                  copyToClipboard(account);
                  helperToast.success(t`Address copied to your clipboard`);
                }}
              >
                <div className="wallet-menu-item transition">
                  <p>
                    <Trans>Copy Address</Trans>
                  </p>

                  <div className="address-icons-copy">
                    <img src={copy} alt="Copy user address" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </button>
    </div>
  );
}

export default AddressDropdown;
